import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import Swal from "sweetalert2";

export default function PortalBilling({ patientId }) {
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingSummary, setBillingSummary] = useState({
    patient_name: "",
    pendingBalance: 0,
    totalPayments: 0,
    totalBilled: 0,
    insuranceBalance: 0,
    lastPaymentDate: "",
  });
  const [InsAmount, setInsAmount] = useState(0);
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState("");
  const [paymentSearchTerm, setPaymentSearchTerm] = useState("");
  const [currentInvoicePage, setCurrentInvoicePage] = useState(1);
  const [currentPaymentPage, setCurrentPaymentPage] = useState(1);
  const invoicesPerPage = 10;
  const paymentsPerPage = 10;

  useEffect(() => {
    if (!patientId) return;
    loadInvoices();
    loadPayments();
    loadBillingSummary();
    InsuranceBalance();
  }, [patientId]);

  const loadInvoices = async () => {
    try {
      const res = await axiosInstance.get(
        `getAllInvoicesByPatient/${patientId}`
      );
      const list = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data)
          ? res.data
          : [];
      setInvoices(list);
    } catch (_) {
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      const res = await axiosInstance.get(`getPaymentsByPatient/${patientId}`);
      const list = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data)
          ? res.data
          : [];
      setPayments(list);
    } catch (_) {
      setPayments([]);
    }
  };

  const loadBillingSummary = async () => {
    try {
      const res = await axiosInstance.get(
        `getPatientPaymentSummary/${patientId}`
      );
      const data = res.data?.data || res.data || {};

      setBillingSummary({
        patient_name: data.patient_name || "",
        pendingBalance: Number(data.pending_balance || 0),
        totalPayments: Number(data.total_payments || 0),
        totalBilled: Number(data.total_billed || 0),
        insuranceBalance: Number(data.insurance_balance || 0),
        lastPaymentDate: data.last_payment_date || "",
      });
    } catch (error) {
      console.error("Error loading billing summary:", error);
    }
  };

  const InsuranceBalance = async () => {
    try {
      const ins = await axiosInstance.get(
        `getInsuranceClaimsByPatientId/${patientId}`
      );
      const amount = Number(
        ins.data?.totalInsuranceAmount ??
        ins.data?.data?.totalInsuranceAmount ??
        0
      );
      setInsAmount(isNaN(amount) ? 0 : amount);
    } catch (error) {
      console.error("Error loading insurance balance:", error);
      setInsAmount(0);
    }
  };

  const handlePrintInvoice = (invoice) => {
    Swal.fire({
      title: "Print Invoice",
      text: `Printing functionality for invoice #${invoice.invoice_no || invoice.invoiceNo || invoice.id} will be available soon.`,
      icon: "info",
    });
  };

  const handlePrintPayment = (payment) => {
    Swal.fire({
      title: "Print Payment",
      text: `Printing functionality for payment will be available soon.`,
      icon: "info",
    });
  };

  // Pagination calculations for invoices
  const filteredInvoices = useMemo(() => {
    if (!invoiceSearchTerm) return invoices;
    const q = invoiceSearchTerm.toLowerCase();
    return invoices.filter(
      (inv) =>
        (inv.invoice_no && inv.invoice_no.toLowerCase().includes(q)) ||
        (inv.invoiceNo && inv.invoiceNo.toLowerCase().includes(q)) ||
        (inv.id && inv.id.toString().toLowerCase().includes(q)) ||
        (inv.total_amount && inv.total_amount.toString().includes(q)) ||
        (inv.paid_amount && inv.paid_amount.toString().includes(q)) ||
        (inv.remaining_amount && inv.remaining_amount.toString().includes(q)) ||
        (inv.invoice_status && inv.invoice_status.toLowerCase().includes(q)) ||
        (inv.status && inv.status.toLowerCase().includes(q))
    );
  }, [invoices, invoiceSearchTerm]);

  const currentInvoices = useMemo(() => {
    const startIndex = (currentInvoicePage - 1) * invoicesPerPage;
    const endIndex = startIndex + invoicesPerPage;
    return filteredInvoices.slice(startIndex, endIndex);
  }, [filteredInvoices, currentInvoicePage, invoicesPerPage]);

  const totalInvoicePages = useMemo(() => {
    return Math.ceil(filteredInvoices.length / invoicesPerPage);
  }, [filteredInvoices, invoicesPerPage]);

  // Pagination calculations for payments
  const filteredPayments = useMemo(() => {
    if (!paymentSearchTerm) return payments;
    const q = paymentSearchTerm.toLowerCase();
    return payments.filter(
      (pay) =>
        (pay.invoice_no && pay.invoice_no.toLowerCase().includes(q)) ||
        (pay.invoice_id &&
          pay.invoice_id.toString().toLowerCase().includes(q)) ||
        (pay.invoiceId && pay.invoiceId.toString().toLowerCase().includes(q)) ||
        (pay.payment_amount && pay.payment_amount.toString().includes(q)) ||
        (pay.amount && pay.amount.toString().includes(q)) ||
        (pay.payment_method && pay.payment_method.toLowerCase().includes(q)) ||
        (pay.method && pay.method.toLowerCase().includes(q)) ||
        (pay.transactionId && pay.transactionId.toLowerCase().includes(q)) ||
        (pay.transaction_id && pay.transaction_id.toLowerCase().includes(q))
    );
  }, [payments, paymentSearchTerm]);

  const currentPayments = useMemo(() => {
    const startIndex = (currentPaymentPage - 1) * paymentsPerPage;
    const endIndex = startIndex + paymentsPerPage;
    return filteredPayments.slice(startIndex, endIndex);
  }, [filteredPayments, currentPaymentPage, paymentsPerPage]);

  const totalPaymentPages = useMemo(() => {
    return Math.ceil(filteredPayments.length / paymentsPerPage);
  }, [filteredPayments, paymentsPerPage]);

  if (loading) return <div className="p-4 text-center">Loading billing info...</div>;

  return (
    <div className="portal-module-container patientCommonCon">
      <div className="row">
        {/* Top summary cards */}
        <div className="col-12 mb-3">
          <div className="row g-3">
            <div className="col-md-3">
              <div className="cardBorder serviceCard h-100 p20">
                <div className="d-flex justify-content-between">
                  <div className="pe-3">
                    <h6>Pending Balance</h6>
                    <h5>{billingSummary.pendingBalance.toFixed(3)} BHD</h5>
                  </div>
                  <div className="avtar avtar-s orangeBgDash">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="9"
                        stroke="#e58a00"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 7v5l3 2"
                        stroke="#e58a00"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M9 17h6"
                        stroke="#e58a00"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="cardBorder serviceCard h-100 p20">
                <div className="d-flex justify-content-between">
                  <div className="pe-3">
                    <h6>Total Payments</h6>
                    <h5>{billingSummary.totalPayments.toFixed(3)} BHD</h5>
                  </div>
                  <div className="avtar avtar-s bg-light-success">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <rect
                        x="3"
                        y="5"
                        width="18"
                        height="14"
                        rx="2"
                        stroke="#2ca87f"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3 10h18"
                        stroke="#2ca87f"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                      <circle cx="8" cy="15" r="1.5" fill="#2ca87f" />
                      <circle cx="12" cy="15" r="1.5" fill="#2ca87f" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="cardBorder serviceCard h-100 p20">
                <div className="d-flex justify-content-between">
                  <div className="pe-3">
                    <h6>Total Billed</h6>
                    <h5>{billingSummary.totalBilled.toFixed(3)} BHD</h5>
                  </div>
                  <div className="avtar avtar-s bg-light-primary ">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M6 3h12a1 1 0 0 1 1 1v16l-4-2-4 2-4-2-4 2V4a1 1 0 0 1 1-1z"
                        stroke="#4680ff"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8 8h8M8 12h5"
                        stroke="#4680ff"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="cardBorder serviceCard h-100 p20">
                <div className="d-flex justify-content-between">
                  <div className="pe-3">
                    <h6>Insurance Balance</h6>
                    <h5>{InsAmount.toFixed(3)} BHD</h5>
                  </div>
                  <div className="avtar avtar-s yellowIcon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M12 2l7 4v6c0 5-3.5 9-7 10-3.5-1-7-5-7-10V6l7-4z"
                        stroke="#daa300"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 8v4"
                        stroke="#daa300"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="15.5" r="0.8" fill="#daa300" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          {/* Invoices Panel */}
          <div className="table-card">
            <div className="tableHeader d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <h5 className="mb-0">My Invoices</h5>
                <div
                  className="tableSearch"
                  style={{ position: "relative", width: "250px" }}
                >
                  <input
                    type="text"
                    placeholder="Search invoices..."
                    className="form-control"
                    value={invoiceSearchTerm}
                    onChange={(e) => setInvoiceSearchTerm(e.target.value)}
                    style={{ paddingLeft: "35px" }}
                  />
                  <span className="searchIcon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="gray"
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "10px",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                      }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="">Inv#</th>
                      <th className="">Date</th>
                      <th className="">Services</th>
                      <th className="">Total Amount</th>
                      <th className="">Paid Amount</th>
                      <th className="">Remaining Patient AMT</th>
                      {/* <th>Added By</th> */}
                      <th className="">Status</th>
                      {/* <th className=" text-center">Action</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {currentInvoices.length === 0 ? (
                      <tr>
                        <td
                          colSpan="9"
                          className="text-center text-muted py-4"
                        >
                          No invoices found
                        </td>
                      </tr>
                    ) : (
                      currentInvoices.map((invoice, index) => (
                        <tr key={index}>
                          <td>
                            {invoice.invoice_no ||
                              invoice.invoiceNo ||
                              invoice.id ||
                              `INV${index + 1}`}
                          </td>
                          <td>
                            {(() => {
                              const ts =
                                invoice.invoice_date ||
                                invoice.updated_at ||
                                invoice.updatedAt ||
                                invoice.date;
                              const d = ts ? new Date(ts) : null;
                              return d && !isNaN(d.getTime())
                                ? d.toLocaleDateString("en-GB")
                                : "-";
                            })()}
                          </td>
                          <td>
                            {[
                              ...(invoice.services || []),
                              ...(invoice.labs || []),
                              ...(invoice.pharmacy || []),
                            ].length > 0
                              ? [
                                ...(invoice.services || []),
                                ...(invoice.labs || []),
                                ...invoice.pharmacy,
                              ]
                                .map((s) => s.serviceName)
                                .join(", ")
                              : "No Services"}
                          </td>
                          <td>
                            {Number(
                              invoice.total_amount ?? invoice.amount ?? 0
                            ).toFixed(3)}{" "}
                            BHD
                          </td>
                          <td>
                            {Number(invoice.paid_amount ?? 0).toFixed(3)} BHD
                          </td>
                          <td>
                            {invoice.remaining_amount != null
                              ? `${Number(invoice.remaining_amount).toFixed(
                                3
                              )} BHD`
                              : "-"}
                          </td>

                          {/* <td>
                            {invoice.sent_by || invoice.created_by || "-"}
                          </td> */}
                          <td>
                            <span
                              className={`badge ${(() => {
                                const status = (
                                  invoice.invoice_status ||
                                  invoice.status ||
                                  ""
                                )
                                  .toString()
                                  .toLowerCase();
                                if (
                                  status === "fully paid" ||
                                  status === "paid"
                                ) {
                                  return "bg-success-subtle text-success";
                                } else if (
                                  status === "not paid" ||
                                  status === "unpaid"
                                ) {
                                  return "bg-danger-subtle text-danger";
                                } else {
                                  return "bg-warning-subtle text-warning";
                                }
                              })()} fw-normal px-3 py-2 rounded-pill`}
                            >
                              {invoice.invoice_status ||
                                invoice.status ||
                                "-"}
                            </span>
                          </td>
                          {/* <td className="text-center">
                            <button
                              className="btn btn-sm deleteIcon"
                              onClick={() => handlePrintInvoice(invoice)}
                              title="Print Invoice"
                            >
                              <i className="ti ti-printer f-20" />
                              <span>Print</span>
                            </button>
                          </td> */}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {totalInvoicePages > 1 && (
                <nav className="tablepagination d-flex justify-content-end mt-3 mb-2">
                  <ul className="pagination mb-0">
                    <li
                      className={`page-item ${currentInvoicePage === 1 ? "disabled" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          currentInvoicePage > 1 &&
                          setCurrentInvoicePage(currentInvoicePage - 1)
                        }
                      >
                        &laquo;
                      </button>
                    </li>

                    <li className="pageNmbr">{`${currentInvoicePage} of ${totalInvoicePages}`}</li>

                    <li
                      className={`page-item ${currentInvoicePage === totalInvoicePages
                        ? "disabled"
                        : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          currentInvoicePage < totalInvoicePages &&
                          setCurrentInvoicePage(currentInvoicePage + 1)
                        }
                      >
                        &raquo;
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
          </div>

          {/* Payment Invoices Panel */}
          <div className="table-card mt-3">
            <div className="tableHeader">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <h5 className="mb-0">My Payment Invoices</h5>
                  <div
                    className="tableSearch"
                    style={{ position: "relative", width: "250px" }}
                  >
                    <input
                      type="text"
                      placeholder="Search payments..."
                      className="form-control"
                      value={paymentSearchTerm}
                      onChange={(e) => setPaymentSearchTerm(e.target.value)}
                      style={{ paddingLeft: "35px" }}
                    />
                    <span className="searchIcon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="gray"
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "10px",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                        }}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="table-responsive w-100">
                <table className="table w-100">
                  <thead>
                    <tr>
                      <th>Sn</th>
                      <th>Invoice ID</th>
                      <th>Date-Time</th>
                      <th>Amount</th>
                      <th>Method</th>
                      {/* <th>Added By</th> */}
                      <th>Transaction</th>
                      {/* <th className="text-center">Action</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {currentPayments.length === 0 ? (
                      <tr>
                        <td
                          colSpan="8"
                          className="text-center text-muted py-4"
                        >
                          No payments found
                        </td>
                      </tr>
                    ) : (
                      currentPayments.map((payment, index) => (
                        <tr key={index}>
                          <td>{payment.sn || index + 1}</td>
                          <td>
                            {payment.invoice_no ||
                              payment.invoice_id ||
                              payment.invoiceId}
                          </td>
                          <td>
                            {(() => {
                              const d =
                                payment.payment_date ||
                                payment.date ||
                                payment.created_at;
                              if (!d) return "-";
                              const date = new Date(d);
                              return `${date.toLocaleDateString("en-US", {
                                month: "2-digit",
                                day: "2-digit",
                                year: "numeric",
                              })} ${date.toLocaleTimeString("en-US", {
                                hour: "numeric",
                                minute: "2-digit",
                                second: "2-digit",
                                hour12: true,
                              })}`;
                            })()}
                          </td>
                          <td>
                            {Number(
                              payment.payment_amount ?? payment.amount ?? 0
                            ).toFixed(3)}{" "}
                            BHD
                          </td>
                          <td>
                            {payment.payment_method || payment.method || "-"}
                          </td>
                          {/* <td>
                            {payment.sent_by || "-"}
                          </td> */}
                          <td>
                            {payment.transactionId ||
                              payment.transaction_id ||
                              payment.transaction ||
                              "-"}
                          </td>
                          {/* <td className="text-center">
                            <button
                              className="btn btn-sm deleteIcon"
                              onClick={() => handlePrintPayment(payment)}
                              title="Print Payment"
                            >
                              <i className="ti ti-printer f-20" />
                              <span>Print</span>
                            </button>
                          </td> */}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {totalPaymentPages > 1 && (
                <nav className="tablepagination d-flex justify-content-end mt-3 mb-2">
                  <ul className="pagination mb-0">
                    <li
                      className={`page-item ${currentPaymentPage === 1 ? "disabled" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          currentPaymentPage > 1 &&
                          setCurrentPaymentPage(currentPaymentPage - 1)
                        }
                      >
                        &laquo;
                      </button>
                    </li>
                    <li className="pageNmbr">{`${currentPaymentPage} of ${totalPaymentPages}`}</li>
                    <li
                      className={`page-item ${currentPaymentPage === totalPaymentPages
                        ? "disabled"
                        : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          currentPaymentPage < totalPaymentPages &&
                          setCurrentPaymentPage(currentPaymentPage + 1)
                        }
                      >
                        &raquo;
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
